import { Link, Locator } from '@readium/shared'
import { useCallback, useMemo } from 'react'

import { Section, SectionListItem, SectionListSheet } from '../../components/section-list'
import { countTocItems, getPageFromLink } from './toc-utils'

type Props = {
  open: boolean
  onClose: () => void
  toc: Link[]
  positions: Locator[]
  numPages: number
  currentLocator: Locator | null
  onNavigate: (href: string) => void
}

/** TOCのhrefと現在のLocatorのhrefを比較 */
const isCurrentChapter = (tocHref: string, currentHref: string | null): boolean => {
  if (!currentHref) return false
  const tocBase = tocHref.split('#')[0].split('/').pop()?.toLowerCase() ?? ''
  const currentBase = currentHref.split('#')[0].split('/').pop()?.toLowerCase() ?? ''
  return tocBase === currentBase
}

/** 子要素に現在の章が含まれているかチェック */
const hasCurrentChapterInChildren = (item: Link, currentHref: string | null): boolean => {
  if (!currentHref || !item.children) return false
  for (const child of item.children.items) {
    if (isCurrentChapter(child.href, currentHref)) return true
    if (hasCurrentChapterInChildren(child, currentHref)) return true
  }
  return false
}

/** Link → Section 変換 */
const linkToSection = (
  item: Link,
  positions: Locator[],
  numPages: number,
  currentHref: string | null,
  tocIndex: number,
  totalTocItems: number,
): Section => {
  const isCurrent = isCurrentChapter(item.href, currentHref)
  const isParentOfCurrent = !isCurrent && hasCurrentChapterInChildren(item, currentHref)
  const page = getPageFromLink(item, positions, numPages, tocIndex, totalTocItems)

  return {
    id: item.href,
    label: item.title ?? item.href,
    href: item.href,
    page,
    isCurrent,
    isParentOfCurrent,
    subitems: item.children?.items.map((child, subIndex) =>
      linkToSection(child, positions, numPages, currentHref, tocIndex + subIndex + 1, totalTocItems),
    ),
  }
}

export const TocDialog = ({ open, onClose, toc, positions, numPages, currentLocator, onNavigate }: Props) => {
  const currentHref = currentLocator?.href ?? null

  const totalTocItems = useMemo(() => countTocItems(toc), [toc])

  const getStartIndex = useCallback(
    (index: number): number => {
      let startIndex = 0
      for (let i = 0; i < index; i++) {
        startIndex += 1
        if (toc[i].children) {
          startIndex += countTocItems(toc[i].children!.items)
        }
      }
      return startIndex
    },
    [toc],
  )

  const sections = useMemo(
    () =>
      toc.map((item, index) =>
        linkToSection(item, positions, numPages, currentHref, getStartIndex(index), totalTocItems),
      ),
    [toc, positions, numPages, currentHref, getStartIndex, totalTocItems],
  )

  return (
    <SectionListSheet open={open} onClose={onClose} title="目次" side="left">
      <div className="space-y-1">
        {sections.map((section) => (
          <SectionListItem key={section.id} item={section} onClick={onNavigate} />
        ))}
        {sections.length === 0 && <p className="text-center text-muted-foreground py-4">目次がありません</p>}
      </div>
    </SectionListSheet>
  )
}
